// src/utils/exportLayerUtils.js
//
// Client-side export of a GeoJSON FeatureCollection to:
//   - GeoJSON   (.geojson)
//   - KML       (.kml)
//   - Shapefile (.zip)  — zero dependencies, pure browser APIs
//
// All exports are purely client-side — no server round-trip, no npm packages.
 
// ── GEOJSON ───────────────────────────────────────────────────────────────────
 
export function exportGeoJSON(geojson, name) {
  const blob = new Blob(
    [JSON.stringify(geojson, null, 2)],
    { type: "application/geo+json" }
  );
  triggerDownload(blob, `${sanitize(name)}.geojson`);
}
 
// ── KML ───────────────────────────────────────────────────────────────────────
 
export function exportKML(geojson, name) {
  const kml = geojsonToKML(geojson, name);
  const blob = new Blob([kml], { type: "application/vnd.google-earth.kml+xml" });
  triggerDownload(blob, `${sanitize(name)}.kml`);
}
 
function geojsonToKML(geojson, name) {
  const placemarks = (geojson.features ?? []).map(featureToPlacemark).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(name)}</name>
    ${placemarks}
  </Document>
</kml>`;
}
 
function featureToPlacemark(f) {
  const props    = f.properties ?? {};
  const label    = props.label ?? props.name ?? props.annotationType ?? "Feature";
  const color    = props.color ?? "#16f2d4";
  const kmlColor = hexToKmlColor(color);
  const geom     = geometryToKML(f.geometry);
  if (!geom) return "";
  return `    <Placemark>
      <name>${escapeXml(label)}</name>
      <Style>
        <LineStyle><color>${kmlColor}</color><width>2</width></LineStyle>
        <PolyStyle><color>33${kmlColor.slice(2)}</color></PolyStyle>
        <IconStyle><color>${kmlColor}</color></IconStyle>
      </Style>
      ${geom}
    </Placemark>`;
}
 
function geometryToKML(geom) {
  if (!geom) return null;
  switch (geom.type) {
    case "Point":
      return `<Point><coordinates>${coordStr([geom.coordinates])}</coordinates></Point>`;
    case "LineString":
      return `<LineString><coordinates>${coordStr(geom.coordinates)}</coordinates></LineString>`;
    case "Polygon":
      return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${
        coordStr(geom.coordinates[0])
      }</coordinates></LinearRing></outerBoundaryIs></Polygon>`;
    case "MultiPolygon":
      return `<MultiGeometry>${
        geom.coordinates.map(poly =>
          `<Polygon><outerBoundaryIs><LinearRing><coordinates>${
            coordStr(poly[0])
          }</coordinates></LinearRing></outerBoundaryIs></Polygon>`
        ).join("")
      }</MultiGeometry>`;
    default:
      return null;
  }
}
 
function coordStr(coords) {
  return coords.map(c => `${c[0]},${c[1]},0`).join(" ");
}
 
function hexToKmlColor(hex) {
  const h = hex.replace("#", "");
  const r = h.slice(0, 2), g = h.slice(2, 4), b = h.slice(4, 6);
  return `ff${b}${g}${r}`;
}
 
function escapeXml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
 
// ── SHAPEFILE — zero-dependency pure browser implementation ───────────────────
//
// Splits the FeatureCollection by geometry type (Point, LineString, Polygon),
// writes a proper .shp + .shx + .dbf + .prj for each group, then packages
// them into a .zip using the ZIP local-file-header format (store, no compress).
// Works in all modern browsers — no CompressionStream needed, no npm packages.
 
export async function exportShapefile(geojson, name) {
  const safe = sanitize(name);
  const features = geojson.features ?? [];
 
  // Split by broad geometry family
  const groups = {
    points:   features.filter(f => f.geometry?.type === "Point"                       || f.geometry?.type === "MultiPoint"),
    lines:    features.filter(f => f.geometry?.type === "LineString"                  || f.geometry?.type === "MultiLineString"),
    polygons: features.filter(f => f.geometry?.type === "Polygon"                     || f.geometry?.type === "MultiPolygon"),
  };
 
  const zipParts = []; // { filename, data: Uint8Array }
 
  for (const [suffix, feats] of Object.entries(groups)) {
    if (!feats.length) continue;
    const baseName = `${safe}_${suffix}`;
 
    const { shp, shx } = buildShp(feats);
    const dbf           = buildDbf(feats);
    const prj           = WGS84_PRJ;
 
    zipParts.push({ filename: `${baseName}.shp`, data: shp });
    zipParts.push({ filename: `${baseName}.shx`, data: shx });
    zipParts.push({ filename: `${baseName}.dbf`, data: dbf });
    zipParts.push({ filename: `${baseName}.prj`, data: strBytes(prj) });
  }
 
  if (!zipParts.length) {
    console.warn("[exportShapefile] no exportable geometry found");
    return;
  }
 
  const zip = buildZip(zipParts);
  triggerDownload(new Blob([zip], { type: "application/zip" }), `${safe}.zip`);
}
 
// ── SHP / SHX builder ────────────────────────────────────────────────────────
 
function buildShp(features) {
  const recBuffers = features.map(f => shpRecord(f.geometry));
 
  // Compute total file length in 16-bit words
  const contentLen = recBuffers.reduce((s, b) => s + 8 + b.byteLength, 0);
  const fileLen    = 50 + contentLen / 2; // header = 50 words
 
  const shpBuf = new ArrayBuffer(100 + contentLen);
  const shxBuf = new ArrayBuffer(100 + features.length * 8);
  const shp    = new DataView(shpBuf);
  const shx    = new DataView(shxBuf);
 
  // Compute MBR across all features
  let [minX, minY, maxX, maxY] = [Infinity, Infinity, -Infinity, -Infinity];
  features.forEach(f => {
    const pts = allPoints(f.geometry);
    pts.forEach(([x, y]) => {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    });
  });
  if (!isFinite(minX)) { minX = minY = maxX = maxY = 0; }
 
  const shapeType = shpTypeFor(features[0]?.geometry?.type);
 
  writeShpHeader(shp, fileLen, shapeType, minX, minY, maxX, maxY);
  writeShpHeader(shx, 50 + features.length * 4, shapeType, minX, minY, maxX, maxY);
 
  let shpOffset = 100;
  let shxOffset = 100;
 
  recBuffers.forEach((rec, i) => {
    const recWords = rec.byteLength / 2;
    // SHP record header (big-endian)
    shp.setInt32(shpOffset,     i + 1,    false); // record number 1-based
    shp.setInt32(shpOffset + 4, recWords, false); // content length in words
    // SHX entry
    shx.setInt32(shxOffset,     shpOffset / 2, false);
    shx.setInt32(shxOffset + 4, recWords,      false);
 
    new Uint8Array(shpBuf, shpOffset + 8, rec.byteLength)
      .set(new Uint8Array(rec));
 
    shpOffset += 8 + rec.byteLength;
    shxOffset += 8;
  });
 
  return {
    shp: new Uint8Array(shpBuf),
    shx: new Uint8Array(shxBuf),
  };
}
 
function writeShpHeader(dv, fileLenWords, shapeType, minX, minY, maxX, maxY) {
  dv.setInt32(0,  9994, false); // file code
  dv.setInt32(24, fileLenWords, false);
  dv.setInt32(28, 1000, true);  // version
  dv.setInt32(32, shapeType, true);
  dv.setFloat64(36, minX, true);
  dv.setFloat64(44, minY, true);
  dv.setFloat64(52, maxX, true);
  dv.setFloat64(60, maxY, true);
  // Z and M ranges = 0
  for (let o = 68; o < 100; o += 8) dv.setFloat64(o, 0, true);
}
 
function shpTypeFor(type) {
  if (!type) return 0;
  if (type === "Point"       || type === "MultiPoint")      return 1;
  if (type === "LineString"  || type === "MultiLineString")  return 3;
  if (type === "Polygon"     || type === "MultiPolygon")     return 5;
  return 0;
}
 
function shpRecord(geom) {
  if (!geom) return new ArrayBuffer(4); // null shape
  switch (geom.type) {
    case "Point":         return shpPoint(geom.coordinates);
    case "MultiPoint":    return shpPoint(geom.coordinates[0]);
    case "LineString":    return shpPolyLine([geom.coordinates]);
    case "MultiLineString": return shpPolyLine(geom.coordinates);
    case "Polygon":       return shpPolygon(geom.coordinates);
    case "MultiPolygon":  return shpPolygon(geom.coordinates.flat());
    default:              return new ArrayBuffer(4);
  }
}
 
function shpPoint([x, y]) {
  const buf = new ArrayBuffer(20);
  const dv  = new DataView(buf);
  dv.setInt32(0, 1, true); // shape type = Point
  dv.setFloat64(4,  x, true);
  dv.setFloat64(12, y, true);
  return buf;
}
 
function shpPolyLine(rings) {
  const numParts  = rings.length;
  const numPoints = rings.reduce((s, r) => s + r.length, 0);
  const buf = new ArrayBuffer(44 + numParts * 4 + numPoints * 16);
  const dv  = new DataView(buf);
  let [minX, minY, maxX, maxY] = mbr(rings.flat());
  dv.setInt32(0,  3, true); // PolyLine
  dv.setFloat64(4, minX, true); dv.setFloat64(12, minY, true);
  dv.setFloat64(20, maxX, true); dv.setFloat64(28, maxY, true);
  dv.setInt32(36, numParts, true);
  dv.setInt32(40, numPoints, true);
  let off = 44, ptOff = 0;
  rings.forEach(r => { dv.setInt32(off, ptOff, true); off += 4; ptOff += r.length; });
  rings.flat().forEach(([x, y]) => {
    dv.setFloat64(44 + numParts * 4 + (ptOff - rings.flat().length + rings.flat().indexOf([x,y])) * 16, x, true);
  });
  // simpler sequential write:
  let ptBase = 44 + numParts * 4;
  rings.flat().forEach(([x, y]) => {
    dv.setFloat64(ptBase, x, true);
    dv.setFloat64(ptBase + 8, y, true);
    ptBase += 16;
  });
  return buf;
}
 
function shpPolygon(rings) {
  // Same binary layout as PolyLine, just shape type = 5
  const buf = shpPolyLine(rings);
  new DataView(buf).setInt32(0, 5, true);
  return buf;
}
 
function mbr(pts) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  pts.forEach(([x, y]) => {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  });
  return [minX, minY, maxX, maxY];
}
 
function allPoints(geom) {
  if (!geom) return [];
  switch (geom.type) {
    case "Point":           return [geom.coordinates];
    case "MultiPoint":
    case "LineString":      return geom.coordinates;
    case "MultiLineString":
    case "Polygon":         return geom.coordinates.flat();
    case "MultiPolygon":    return geom.coordinates.flat(2);
    default:                return [];
  }
}
 
// ── DBF builder (minimal — exports label + annotationType + color) ────────────
 
function buildDbf(features) {
  const fields = [
    { name: "label",      type: "C", len: 64 },
    { name: "ann_type",   type: "C", len: 32 },
    { name: "color",      type: "C", len: 16 },
  ];
 
  const headerSize = 32 + fields.length * 32 + 1;
  const recSize    = 1 + fields.reduce((s, f) => s + f.len, 0);
  const buf        = new Uint8Array(headerSize + features.length * recSize);
  const dv         = new DataView(buf.buffer);
 
  // DBF header
  buf[0]  = 3;                          // version
  const now = new Date();
  buf[1] = now.getFullYear() - 1900;
  buf[2] = now.getMonth() + 1;
  buf[3] = now.getDate();
  dv.setUint32(4,  features.length, true);
  dv.setUint16(8,  headerSize, true);
  dv.setUint16(10, recSize, true);
 
  // Field descriptors
  let fieldOff = 32;
  fields.forEach(f => {
    const nameBytes = strBytes(f.name.padEnd(11, "\0").slice(0, 11));
    buf.set(nameBytes, fieldOff);
    buf[fieldOff + 11] = f.type.charCodeAt(0);
    buf[fieldOff + 16] = f.len;
    fieldOff += 32;
  });
  buf[fieldOff] = 0x0D; // header terminator
 
  // Records
  let recOff = headerSize;
  features.forEach(f => {
    buf[recOff++] = 0x20; // deletion flag = space (not deleted)
    const props = f.properties ?? {};
    [
      (props.label      ?? "").slice(0, 64),
      (props.annotationType ?? "").slice(0, 32),
      (props.color      ?? "").slice(0, 16),
    ].forEach((val, i) => {
      const bytes = strBytes(val.padEnd(fields[i].len, " ").slice(0, fields[i].len));
      buf.set(bytes, recOff);
      recOff += fields[i].len;
    });
  });
 
  return buf;
}
 
// ── ZIP builder (store, no compression — works without CompressionStream) ─────
 
function buildZip(parts) {
  // Each part: { filename, data: Uint8Array }
  const enc      = new TextEncoder();
  const localHdrs = [];
  let   offset    = 0;
 
  const localBufs = parts.map(({ filename, data }) => {
    const nameBytes = enc.encode(filename);
    const crc       = crc32(data);
    const hdr = new Uint8Array(30 + nameBytes.length);
    const dv  = new DataView(hdr.buffer);
    dv.setUint32(0,  0x04034b50, false); // local file signature
    dv.setUint16(4,  20, true);          // version needed
    dv.setUint16(6,  0,  true);          // flags
    dv.setUint16(8,  0,  true);          // compression = store
    dv.setUint16(10, 0,  true);          // mod time
    dv.setUint16(12, 0,  true);          // mod date
    dv.setUint32(14, crc,         true);
    dv.setUint32(18, data.length, true); // compressed size
    dv.setUint32(22, data.length, true); // uncompressed size
    dv.setUint16(26, nameBytes.length, true);
    dv.setUint16(28, 0, true);           // extra field length
    hdr.set(nameBytes, 30);
 
    localHdrs.push({ nameBytes, crc, size: data.length, offset });
    offset += hdr.length + data.length;
    return [hdr, data];
  });
 
  // Central directory
  const cdBufs = localHdrs.map(({ nameBytes, crc, size, offset: lOffset }) => {
    const cd = new Uint8Array(46 + nameBytes.length);
    const dv = new DataView(cd.buffer);
    dv.setUint32(0,  0x02014b50, false); // central dir signature
    dv.setUint16(4,  20, true);
    dv.setUint16(6,  20, true);
    dv.setUint16(8,  0,  true);
    dv.setUint16(10, 0,  true);
    dv.setUint16(12, 0,  true);
    dv.setUint16(14, 0,  true);
    dv.setUint32(16, crc,  true);
    dv.setUint32(20, size, true);
    dv.setUint32(24, size, true);
    dv.setUint16(28, nameBytes.length, true);
    dv.setUint16(30, 0, true);
    dv.setUint16(32, 0, true);
    dv.setUint16(34, 0, true);
    dv.setUint16(36, 0, true);
    dv.setUint32(38, 0, true);
    dv.setUint32(42, lOffset, true);
    cd.set(nameBytes, 46);
    return cd;
  });
 
  const cdSize   = cdBufs.reduce((s, b) => s + b.length, 0);
  const cdOffset = offset;
 
  // End of central directory
  const eocd = new Uint8Array(22);
  const eodv = new DataView(eocd.buffer);
  eodv.setUint32(0,  0x06054b50, false);
  eodv.setUint16(4,  0, true);
  eodv.setUint16(6,  0, true);
  eodv.setUint16(8,  parts.length, true);
  eodv.setUint16(10, parts.length, true);
  eodv.setUint32(12, cdSize,   true);
  eodv.setUint32(16, cdOffset, true);
  eodv.setUint16(20, 0, true);
 
  // Concatenate everything
  const all = [
    ...localBufs.flat(),
    ...cdBufs,
    eocd,
  ];
  const total  = all.reduce((s, b) => s + b.length, 0);
  const result = new Uint8Array(total);
  let pos = 0;
  all.forEach(b => { result.set(b, pos); pos += b.length; });
  return result;
}
 
// CRC-32 table
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[i] = c;
  }
  return t;
})();
 
function crc32(data) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}
 
const WGS84_PRJ = `GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137.0,298.257223563]],PRIMEM["Greenwich",0.0],UNIT["Degree",0.0174532925199433]]`;
 
// ── SHARED HELPERS ────────────────────────────────────────────────────────────
 
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href     = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
 
function sanitize(name) {
  return (name ?? "layer").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 60);
}
 
function strBytes(str) {
  return new TextEncoder().encode(str);
}